import React, { useState } from "react";
import { Modal, Button, Row, Col, Form, ListGroup, Badge } from "react-bootstrap";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import { approveRejectRequest } from "../../../services/participantServices/ParticipantService";
import { useAuth } from "../../../firebase/AuthProvider";
import { AppEvents } from "../../../events/events";

interface Props {
  show: boolean;
  onHide: () => void;
  selectedRequest: any | null;
  onCompleted: () => void; // callback to parent to refresh list
}

const ParticipantRequestModal: React.FC<Props> = ({
  show,
  onHide,
  selectedRequest,
  onCompleted,
}) => {
    const {currentUser} = useAuth();
  const [rejectionReason, setRejectionReason] = useState("");
  const [loading, setLoading] = useState(false);

  const renderField = (label: string, oldVal: any, newVal: any) => {
    const oldStr = oldVal ?? "";
    const newStr = newVal ?? "";
    const changed = oldStr !== newStr;
    return (
      <ListGroup.Item key={label} className="py-2">
        <Row className="align-items-center">
          <Col xs={2} className="text-muted small">
            {label}
          </Col>
          <Col xs={5}>
            {changed ? (
              <Badge bg="danger" className="text-truncate d-inline-block" style={{ maxWidth: "100%" }}>
                {oldStr}
              </Badge>
            ) : (
              <span className="text-truncate d-inline-block" style={{ maxWidth: "100%" }}>{oldStr}</span>
            )}
          </Col>
          <Col xs={5}>
            {changed ? (
              <Badge bg="success" className="text-truncate d-inline-block" style={{ maxWidth: "100%" }}>
                {newStr}
              </Badge>
            ) : (
              <span className="text-truncate d-inline-block" style={{ maxWidth: "100%" }}>{newStr}</span>
            )}
          </Col>
        </Row>
      </ListGroup.Item>
    );
  };

  const fields = selectedRequest
    ? [
        {
          label: "Name",
          oldValue: `${selectedRequest.orignalProfile?.firstName || ""} ${selectedRequest.orignalProfile?.lastName || ""}`.trim(),
          newValue: `${selectedRequest.newProfileChanegs?.firstName || ""} ${selectedRequest.newProfileChanegs?.lastName || ""}`.trim(),
        },
        { label: "Email", oldValue: selectedRequest.orignalProfile?.email, newValue: selectedRequest.newProfileChanegs?.email },
        { label: "Number", oldValue: selectedRequest.orignalProfile?.mobileNumber, newValue: selectedRequest.newProfileChanegs?.mobileNumber },
        { label: "Country", oldValue: selectedRequest.orignalProfile?.countryName, newValue: selectedRequest.newProfileChanegs?.countryName },
        { label: "Waiver", oldValue: selectedRequest.orignalProfile?.waiverName, newValue: selectedRequest.newProfileChanegs?.waiverName },
        { label: "ParticipantSsnLast4", oldValue: selectedRequest.orignalProfile?.participantSsnLast4, newValue: selectedRequest.newProfileChanegs?.participantSsnLast4 },
      ]
    : [];

  const handleApprove = async () => {
    if (!selectedRequest) return;
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You are about to approve this request.",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, Approve",
      cancelButtonText: "Cancel",
    });

    if (!result.isConfirmed) return;
    if (!currentUser) return;

    try {
      setLoading(true);
      const token = await currentUser.getIdToken();
      const requestId = selectedRequest.transationRequestId;

      const success = await approveRejectRequest(
        requestId,
        {
          action: "approve",
          reason: "",
          id: selectedRequest.id,
          participantId: selectedRequest.participantId,
          firstName: selectedRequest.newProfileChanegs.firstName,
          lastName: selectedRequest.newProfileChanegs.lastName,
          email: selectedRequest.newProfileChanegs.email,
          mobileNumber: selectedRequest.newProfileChanegs.mobileNumber,
          countryName: selectedRequest.newProfileChanegs.countryName,
          waiverId: selectedRequest.newProfileChanegs.waiverId,
          countryId: selectedRequest.newProfileChanegs.countryId,
          regionId: selectedRequest.newProfileChanegs.regionId,
          participantSsnLast4: selectedRequest.newProfileChanegs.participantSsnLast4,
        },
        token
      );

      if (success) {
        AppEvents.emit('UPDATE_ADMIN_REQUEST_COUNT');
        Swal.fire("Approved!", "The request has been approved.", "success");
        onHide();
        onCompleted();
      } else {
        Swal.fire("Error", "Failed to approve request.", "error");
      }
    } catch (err: any) {
      console.error("Approve failed:", err);
      Swal.fire("Error", "An unexpected error occurred.", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleReject = async () => {
    if (!selectedRequest) return;
    if (!rejectionReason || rejectionReason.trim().length === 0) {
      toast.warning("Please enter a reason for rejection.");
      return;
    }

    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You are about to reject this request.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, Reject",
      cancelButtonText: "Cancel",
    });

    if (!result.isConfirmed) return;
    if (!currentUser) return;

    try {
      setLoading(true);
      const token = await currentUser.getIdToken();
      const requestId = selectedRequest.transationRequestId;

      const success = await approveRejectRequest(
        requestId,
        {
          action: "reject",
          reason: rejectionReason,
          id: selectedRequest.id,
          participantId: selectedRequest.participantId,
          firstName: selectedRequest.newProfileChanegs?.firstName,
          lastName: selectedRequest.newProfileChanegs?.lastName,
          email: selectedRequest.newProfileChanegs?.email,
          mobileNumber: selectedRequest.newProfileChanegs?.mobileNumber,
          countryName: selectedRequest.newProfileChanegs?.countryName,
          waiverId: selectedRequest.newProfileChanegs?.waiverId,
          countryId: selectedRequest.newProfileChanegs?.countryId,
          regionId: selectedRequest.newProfileChanegs?.regionId,
        },
        token
      );

      if (success) {
        AppEvents.emit('UPDATE_ADMIN_REQUEST_COUNT');
        toast.success(success.message || "Request rejected.");
        onHide();
        onCompleted();
        setRejectionReason("");
      } else {
        Swal.fire("Error", "Failed to reject request.", "error");
      }
    } catch (err: any) {
      console.error("Reject failed:", err);
      Swal.fire("Error", "An unexpected error occurred.", "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered className="custom_popup" size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Participant Request Details</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {!selectedRequest ? (
          <div>No request selected.</div>
        ) : (
          <>
            <Row className="mb-3 gx-2 align-items-center">
              <Col xs="auto" className="d-flex align-items-center">
                <Badge bg="danger" style={{ width: 12, height: 12, borderRadius: 12 }}>&nbsp;</Badge>
                <small className="ms-2 text-muted">Old Value</small>
              </Col>
              <Col xs="auto" className="d-flex align-items-center">
                <Badge bg="success" style={{ width: 12, height: 12, borderRadius: 12 }}>&nbsp;</Badge>
                <small className="ms-2 text-muted">Updated Value</small>
              </Col>
            </Row>

            <ListGroup className="mb-3">
              {fields.map((f) => renderField(f.label, f.oldValue, f.newValue))}
            </ListGroup>

            <p className="mb-2">
              <strong>Requested On:</strong>{" "}
              {selectedRequest.requestTime ? new Date(selectedRequest.requestTime).toLocaleString() : "N/A"}
            </p>

            <Form.Group>
              <Form.Label><strong>Comment (optional):</strong></Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="Write your reason here..."
              />
            </Form.Group>
          </>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button
          variant="secondary"
          onClick={handleApprove}
          disabled={!selectedRequest || loading}
        >
          Approve
        </Button>
        <Button
          variant="danger"
          onClick={handleReject}
          disabled={!selectedRequest || loading}
        >
          Reject
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ParticipantRequestModal;